// src/pages/AddEditBulkVaultQuestions.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  setParsedRows,
  setValidateErrors,
  updateRow,
  resetBulkUpload,
} from "../../redux/vaultQuestions/vaultQuestionSlice";
import { Icon } from "@iconify/react/dist/iconify.js";
import { saveBulkVaultQuestions } from "../../redux/vaultQuestions/vaultQuestionThunks";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";

/**
 * Renders cards for each parsed row (question)
 * Inline editing, selection, final save
 */
export default function AddEditBulkVaultQuestions() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const parsedRows = useSelector(
    (state) => state.vaultQuestion.parsedRows || []
  );

  const parseErrors = useSelector(
    (state) => state.vaultQuestion.parseErrors || []
  );
  const validateErrors = useSelector(
    (state) => state.vaultQuestion.validateErrors || []
  );
  const loading = useSelector((state) => state.vaultQuestion.loading);
  const saveResult = useSelector((state) => state.vaultQuestion.saveResult);
  const saveError = useSelector((state) => state.vaultQuestion.saveError);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!parsedRows || parsedRows.length === 0) {
      setTimeout(() => {
        navigate("/vault-questions");
      }, 2000);
      return;
    }
    // seed local rows: add `selected` flag and normalize IsActive boolean
    const seeded = parsedRows.map((r) => ({
      ...r,
      __raw: r,
      selected: true,
      IsActive: normalizeIsActive(r.IsActive),
    }));
    setRows(seeded);
  }, [parsedRows, navigate]);

  useEffect(() => {
    if (saveResult && saveResult.success) {
      dispatch(resetBulkUpload());

      setTimeout(() => {
        navigate("/vault-questions");
      }, 2000);
    }
  }, [saveResult, dispatch, navigate]);

  function normalizeIsActive(val) {
    if (val === true || val === false) return val;
    const s = String(val || "")
      .trim()
      .toLowerCase();
    return ["true", "yes", "1"].includes(s);
  }

  const toggleSelect = (i) => {
    setRows((prev) =>
      prev.map((r, idx) => (idx === i ? { ...r, selected: !r.selected } : r))
    );
  };

  const handleFieldChange = (i, field, value) => {
    setRows((prev) => {
      const copy = [...prev];
      const updatedRow = { ...copy[i], [field]: value };

      // ✅ Special case: answers field affects questionType
      if (field === "answers") {
        // Always keep answers as a string
        updatedRow.answers = value;

        // Auto-assign type if not set
        if (updatedRow.answers && !updatedRow.questionType) {
          updatedRow.questionType = "multiple_choice";
        }

        // If answers cleared, reset type
        if (
          (!updatedRow.answers || updatedRow.answers.trim() === "") &&
          updatedRow.questionType === "multiple_choice"
        ) {
          updatedRow.questionType = "";
        }
      }

      // ✅ Clear that field's error if now valid
      if (updatedRow.errors?.[field]) {
        let isValid = true;

        if (field === "tags") {
          const tagsArray = value
            ? value
                .split("; ")
                .map((t) => t.trim())
                .filter(Boolean)
            : [];
          isValid = tagsArray.length > 0;
        } else if (field === "answers") {
          const answersArr = updatedRow.answers
            ? updatedRow.answers
                .split(";")
                .map((a) => a.trim())
                .filter(Boolean)
            : [];
          isValid = answersArr.length > 0;

          // Only clear questionType error if answers are non-empty
          if (
            updatedRow.questionType === "multiple_choice" &&
            updatedRow.errors?.questionType &&
            answersArr.length > 0
          ) {
            updatedRow.errors = { ...updatedRow.errors };
            delete updatedRow.errors.questionType;
          }
        } else if (field === "questionType") {
          isValid = !!value && value.trim() !== "";
        } else if (!value || String(value).trim() === "") {
          isValid = false;
        }

        if (isValid) {
          updatedRow.errors = { ...updatedRow.errors };
          delete updatedRow.errors[field];
        }
      }

      copy[i] = updatedRow;
      dispatch(updateRow({ index: i, row: updatedRow }));

      return copy;
    });
  };

  const validateRows = (rows) => {
    return rows.map((row) => {
      const errors = {};

      // Question Text
      if (!row.questionText || String(row.questionText).trim() === "") {
        errors.questionText = "Question text is required";
      }
      let questionType = row.questionType;

      // ✅ Fallback: if answers exist but type not set, auto-assign multiple_choice
      if (
        (!questionType || questionType.trim() === "") &&
        row.answers &&
        row.answers.length > 0
      ) {
        questionType = "multiple_choice";
      }

      // Question Type
      if (!row.questionType || String(row.questionType).trim() === "") {
        errors.questionType = "Question type is required";
      }

      // Answers for multiple choice
      let cleanAnswers = [];
      if (questionType === "multiple_choice") {
        if (!row.answers || String(row.answers).trim() === "") {
          errors.answers = ["Answers are required"];
        } else {
          let answersArr = Array.isArray(row.answers)
            ? row.answers.map((a) => String(a).trim())
            : String(row.answers)
                .split(";")
                .map((a) => a.trim());

          // prepare an array same length as answersArr
          let answerErrs = new Array(answersArr.length).fill(null);

          // ❌ Empty fields
          answersArr.forEach((a, idx) => {
            if (a === "") {
              answerErrs[idx] = "This answer cannot be empty.";
            }
          });

          // ✅ Only check non-empty answers for length/letters
          answersArr.forEach((a, idx) => {
            if (a && (a.length < 5 || !/[A-Za-z]/.test(a))) {
              answerErrs[idx] =
                "Answer must be at least 5 characters and contain letters.";
            }
          });

          // set cleanAnswers but keep blanks (so UI doesn't drop them)
          cleanAnswers = answersArr;

          // If any errors, attach them
          if (answerErrs.some(Boolean)) {
            errors.answers = answerErrs;
          }

          // Final fallback: if all answers invalid/empty
          if (cleanAnswers.filter((a) => a !== "").length === 0) {
            errors.answers = ["At least one valid answer is required"];
          }
        }
      }

      // Tier
      if (!row.tier || String(row.tier).trim() === "") {
        errors.tier = "Tier is required";
      }

      // Tags
      const tagsArray = row.tags
        ? row.tags
            .split(";")
            .map((t) => t.trim())
            .filter(Boolean)
        : [];
      if (tagsArray.length === 0) {
        errors.tags = "Tags are required";
      }

      return {
        ...row,
        answers: cleanAnswers,
        errors,
        selected: row.selected,
      };
    });
  };

  const handleSaveSelected = async () => {
    const validatedRows = validateRows(rows);
    setRows(validatedRows); // updates UI with errors
    const selected = validatedRows
      .map((r) => ({ ...r }))
      .filter((r) => r.selected)
      .map((r) => {
        return {
          ...r.__raw,
          questionText: r.questionText,
          answers: r.answers,
          tier: r.tier,
          tags: r.tags,
          questionType: r.questionType,
          sensitivityLevel: r.sensitivityLevel,
          aiScore: r.aiScore,
          aiSource: r.aiSource,
          status: r.status,
          reviewStatus: r.reviewStatus,
          isActive: r.isActive,
          // include any other metadata if present
          // cautious: __raw may include original columns
        };
      });

    if (selected.length === 0) {
      setErrorMessage("No questions selected to save");
      return;
    }

    const hasErrors = validatedRows.some((r) => {
      if (!r.selected) return false;
      if (!r.errors) return false;
      return Object.values(r.errors).some((err) => {
        if (Array.isArray(err)) return err.some(Boolean); // per-answer errors
        return Boolean(err); // string or other error
      });
    });

    if (hasErrors) {
      setErrorMessage("Please fill in all required fields.");
      setTimeout(() => {
        setErrorMessage("");
      }, 3000);
      return; // ✅ stop save if any validation error
    }

    // Optionally: do a final client-side validation here (ensure required fields)
    const errors = [];
    selected.forEach((s, idx) => {
      const line = idx + 1;
      const rowErrs = [];
      if (!s.questionText || String(s.questionText).trim() === "")
        rowErrs.push("questionText required");
      // Question type required
      if (!s.questionType || String(s.questionType).trim() === "") {
        if (s.answers && s.answers.length > 0) {
          s.questionType = "multiple_choice"; // auto-fix
        } else {
          rowErrs.push("questionType required");
        }
      }
      if (s.questionType === "multiple_choice") {
        if (!s.answers || String(s.answers).trim() === "") {
          rowErrs.push("answers required");
        }
      }
      if (!s.tier || String(s.tier).trim() === "")
        rowErrs.push("Tier required");
      // Tags required
      if (
        !s.tags ||
        (Array.isArray(s.tags)
          ? s.tags.length === 0
          : String(s.tags).trim() === "")
      ) {
        rowErrs.push("tags required");
      }

      if (rowErrs.length) errors.push({ line, messages: rowErrs });
    });
    if (errors.length) {
      // Store validation errors keyed by row index
      const errorMap = {};
      errors.forEach((err) => {
        errorMap[err.line - 1] = err.messages; // line-1 is actual index in rows array
      });
      dispatch(setValidateErrors(errorMap));
      setErrorMessage("Please fill in all required fields.");
      setTimeout(() => {
        setErrorMessage("");
      }, 3000);
      return;
    }

    // Dispatch thunk to save, wrapping selected array inside { questions: ... }

    try {
      const res = await dispatch(
        saveBulkVaultQuestions({ questions: selected })
      ).unwrap();

      let msg = "Selected questions saved successfully!";
      if (res?.success === true) {
        // msg = `Saved successfully! Inserted: ${res.insertedCount}, Updated: ${res.updatedCount}`;
        setSuccessMessage(msg);
      }
    } catch (err) {
      const errorMsg =
        typeof err === "string"
          ? err
          : err?.message || "Failed to save selected questions.";
      setErrorMessage(errorMsg);
    } finally {
      // Auto clear after few seconds
      setTimeout(() => {
        setSuccessMessage("");
        setErrorMessage("");
      }, 3000);
    }
  };

  // Helper: generate a pastel-ish color from a string
  const getTagColor = (tag) => {
    let hash = 0;
    for (let i = 0; i < tag.length; i++) {
      hash = tag.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = hash % 360;
    return `hsl(${hue}, 70%, 80%)`; // pastel colors
  };
  return (
    <div style={{ padding: 20 }}>
      {successMessage && (
        <SuccessAlert
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}
      {errorMessage && (
        <DengerAlert
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}
      {parseErrors.length > 0 && (
        <div style={{ background: "#fff4f0", padding: 10, borderRadius: 6 }}>
          <strong>Parse errors:</strong>
          <ul>
            {parseErrors.map((p, idx) => (
              <li key={idx}>
                Line {p.line || "?"}: {p.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Validation Errors Uncomment when needed */}

      {/* {validateErrors.length > 0 && (
        <div
          style={{
            background: "#fff4f0",
            padding: 10,
            borderRadius: 6,
            marginTop: 10,
          }}
        >
          <strong>Validation errors:</strong>
          <ul>
            {validateErrors.map((v, idx) => (
              <li key={idx}>
                Line {v.line}: {v.messages.join(", ")}
              </li>
            ))}
          </ul>
        </div>
      )} */}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
          marginTop: 16,
        }}
      >
        {rows.map((row, i) => (
          <div key={i} className="card mb-4 p-3">
            {/* Include Checkbox */}
            <div className="form-check mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id={`include-${i}`}
                checked={row.selected}
                onChange={() => toggleSelect(i)}
              />
            </div>

            {/* Tier Dropdown */}
            <div className="mb-3">
              <label htmlFor={`tier-${i}`} className="form-label fw-semibold">
                Tier
              </label>
              <select
                id={`tier-${i}`}
                className={`form-select ${
                  row.selected && row.errors?.tier ? "is-invalid" : ""
                }`}
                value={row.tier}
                onChange={(e) => handleFieldChange(i, "tier", e.target.value)}
              >
                <option value="">Select Tier</option>
                <option value="iceBreaker">iceBreaker</option>
                <option value="deeperDive">deeperDive</option>
                <option value="wildCard">wildCard</option>
                <option value="dailyQuestion">Daily Questions</option>
                <option value="keptTogether">Kept, Together</option>
                <option value="gatekeeper">gatekeeper</option>
              </select>
              {row.selected && row.errors?.tier && (
                <div className="invalid-feedback">{row.errors.tier}</div>
              )}
            </div>
            {/* Type Dropdown */}

            <div className="mb-3">
              <label
                htmlFor={`questionType-${i}`}
                className="form-label fw-semibold"
              >
                Question Type
              </label>
              <select
                id={`type-${i}`}
                className={`form-select ${
                  row.selected && row.errors?.questionType ? "is-invalid" : ""
                }`}
                value={row.questionType}
                onChange={(e) =>
                  handleFieldChange(i, "questionType", e.target.value)
                }
              >
                <option value="">Select Question Type</option>
                <option value="multiple_choice">Multiple Choice</option>
                <option value="free_text">Free Text</option>
              </select>
              {row.selected && row.errors?.questionType && (
                <div className="invalid-feedback">
                  {row.errors.questionType}
                </div>
              )}
            </div>

            {/* Question Text */}
            <div className="mb-3">
              <label
                htmlFor={`question-text-${i}`}
                className="form-label fw-semibold"
              >
                Question Text
              </label>
              <input
                type="text"
                id={`question-text-${i}`}
                className={`form-control ${
                  row.selected && row.errors?.questionText ? "is-invalid" : ""
                }`}
                value={row.questionText}
                onChange={(e) =>
                  handleFieldChange(i, "questionText", e.target.value)
                }
                onInput={(e) => {
                  e.target.value = e.target.value
                    .replace(/^\s+/, "")
                    .replace(/\s{2,}/g, " ");
                }}
              />
              {row.selected && row.errors?.questionText && (
                <div className="invalid-feedback">
                  {row.errors.questionText}
                </div>
              )}
            </div>

            {/* Tags (tag input with colors) */}
            <div className="mb-3">
              <label htmlFor={`tags-${i}`} className="form-label fw-semibold">
                Tags
              </label>
              <div
                className={`form-control d-flex flex-wrap align-items-center tags-container ${
                  row.selected && row.errors?.tags ? "is-invalid" : ""
                }`}
                style={{ gap: "6px", minHeight: "38px", appearance: "none" }}
                onClick={(e) => e.currentTarget.querySelector("input").focus()}
                onInput={(e) => {
                  e.target.value = e.target.value
                    .replace(/^\s+/, "")
                    .replace(/\s{2,}/g, " ");
                }}
              >
                {(row.tags
                  ? row.tags
                      .split(";")
                      .map((t) => t.trim())
                      .filter(Boolean)
                  : []
                ).map((tag, tagIdx) => (
                  <span
                    key={tagIdx}
                    className="badge d-flex align-items-center"
                    style={{
                      gap: "4px",
                      backgroundColor: getTagColor(tag),
                      color: "#333", // dark text for contrast
                    }}
                  >
                    {tag}
                    <Icon
                      icon="ic:baseline-close"
                      className="cursor-pointer"
                      onClick={() => {
                        const newTags = row.tags
                          ? row.tags
                              .split(";")
                              .map((t) => t.trim())
                              .filter(Boolean)
                          : [];
                        newTags.splice(tagIdx, 1);
                        handleFieldChange(i, "tags", newTags.join("; "));
                      }}
                    />
                  </span>
                ))}

                <input
                  type="text"
                  className="border-0 flex-grow-1"
                  style={{ outline: "none", minWidth: "100px" }}
                  placeholder="Type and press Enter"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      const newTag = e.target.value.trim();
                      if (newTag) {
                        const newTags = row.tags
                          ? row.tags
                              .split(";")
                              .map((t) => t.trim())
                              .filter(Boolean)
                          : [];
                        if (!newTags.includes(newTag)) {
                          newTags.push(newTag);
                          handleFieldChange(i, "tags", newTags.join("; "));
                        }
                      }
                      e.target.value = "";
                    } else if (e.key === "Backspace" && !e.target.value) {
                      // Remove last tag if input is empty
                      const newTags = row.tags
                        ? row.tags
                            .split(";")
                            .map((t) => t.trim())
                            .filter(Boolean)
                        : [];
                      if (newTags.length > 0) {
                        newTags.pop();
                        handleFieldChange(i, "tags", newTags.join("; ")); // ✅ update row state
                      }
                    }
                  }}
                />
              </div>
              {/* Error message */}
              {row.selected && row.errors?.tags && (
                <div className="invalid-feedback d-block">
                  {row.errors.tags}
                </div>
              )}
            </div>

            {/* Answer Options */}
            {(row.questionType === "multiple_choice" ||
              (!row.questionType && row.answers && row.answers.length > 0)) && (
              <div className="mb-3">
                <label className="form-label fw-semibold">Answers</label>
                {(() => {
                  // Extract answers array once
                  let answersArr = [];
                  if (Array.isArray(row.answers)) {
                    answersArr = row.answers;
                  } else if (
                    typeof row.answers === "string" &&
                    row.answers.trim() !== ""
                  ) {
                    answersArr = row.answers.split("; ");
                  }
                  if (
                    answersArr.length === 0 &&
                    row.questionType === "multiple_choice"
                  ) {
                    answersArr = [""];
                  }

                  return answersArr.map((ans, ansIdx) => (
                    <div key={ansIdx} className="mb-2 align-items">
                      {/* Input + Button in one row */}
                      <div className="d-flex align-items-center">
                        <input
                          type="text"
                          className={`form-control ${
                            row.selected &&
                            row.errors?.answers &&
                            row.errors.answers[ansIdx]
                              ? "is-invalid"
                              : ""
                          }`}
                          value={ans}
                          onChange={(e) => {
                            const newAnswers = [...answersArr];
                            newAnswers[ansIdx] = e.target.value;
                            handleFieldChange(
                              i,
                              "answers",
                              newAnswers.join("; ")
                            );
                          }}
                          onInput={(e) => {
                            e.target.value = e.target.value
                              .replace(/^\s+/, "")
                              .replace(/\s{2,}/g, " ");
                          }}
                        />

                        {answersArr.length > 1 && (
                          <button
                            className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                            type="button"
                            onClick={() => {
                              const newAnswers = [...answersArr];
                              newAnswers.splice(ansIdx, 1);
                              handleFieldChange(
                                i,
                                "answers",
                                newAnswers.join("; ")
                              );
                            }}
                          >
                            <Icon
                              icon="ic:baseline-minus"
                              className="icon text-xl line-height-1"
                            />
                          </button>
                        )}
                      </div>
                      {/* Inline error message per answer, if exists */}
                      {row.selected &&
                        row.errors?.answers &&
                        row.errors.answers[ansIdx] && (
                          <div className="invalid-feedback d-block">
                            {row.errors.answers[ansIdx]}
                          </div>
                        )}
                    </div>
                  ));
                })()}

                <button
                  className="btn btn-primary text-sm btn-sm px-12 py-12 mt-10 radius-8 d-flex align-items-center gap-2"
                  type="button"
                  onClick={() => {
                    let answersArr = [];

                    if (Array.isArray(row.answers)) {
                      answersArr = [...row.answers]; // copy array
                    } else if (
                      typeof row.answers === "string" &&
                      row.answers.trim() !== ""
                    ) {
                      answersArr = row.answers.split("; ");
                    }

                    // If no answers exist, start with one empty field
                    if (answersArr.length === 0) {
                      answersArr = [""];
                      handleFieldChange(i, "answers", answersArr.join("; "));
                      return;
                    }

                    // Validate last answer before adding new one
                    const lastAnswer = answersArr[answersArr.length - 1];
                    if (!lastAnswer || lastAnswer.trim() === "") {
                      // Inject inline error for last field
                      const newErrors = {
                        ...row.errors,
                        answers: Array.isArray(row.errors?.answers)
                          ? [...row.errors.answers]
                          : [],
                      };
                      newErrors.answers[answersArr.length - 1] =
                        "Please fill this answer before adding another.";

                      handleFieldChange(i, "errors", newErrors);
                      return;
                    }

                    // Add new empty answer
                    answersArr.push("");
                    handleFieldChange(i, "answers", answersArr.join("; "));
                  }}
                >
                  <Icon
                    icon="ic:baseline-plus"
                    className="icon text-xl line-height-1"
                  />
                  Add Answer
                </button>

                {/* If there are any global answer errors not tied to specific answers */}
                {row.errors?.answers && !Array.isArray(row.errors.answers) && (
                  <div className="text-danger mt-1">{row.errors.answers}</div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 20 }}>
        <button
          className="btn btn-primary mt-3 float-end"
          onClick={handleSaveSelected}
          disabled={loading}
        >
          {loading ? "Saving..." : "Save Selected"}
        </button>

        {saveError && (
          <div style={{ marginTop: 10, color: "red" }}>
            {JSON.stringify(saveError)}
          </div>
        )}
      </div>
    </div>
  );
}
